import React, { useEffect, useState, useCallback } from "react";
import axios from 'axios';
import { Pencil, Trash2, ChevronDown, ChevronUp, CalendarPlus, Search, User, BookOpen, PlusCircle, FileText, ExternalLink } from "lucide-react";

export default function VideoClassManagement() {
  const [courses, setCourses] = useState([]);
  const [videoClasses, setVideoClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const StudentsPerPage = 16;

  // --- Student Modal Tabs & Paid Courses States ---
  const [studentModalTab, setStudentModalTab] = useState('classes'); // 'classes' | 'payment'
  const [studentPaidCourses, setStudentPaidCourses] = useState([]);
  const [loadingPaidCourses, setLoadingPaidCourses] = useState(false);

  // --- Student Performance Tracker Modal States ---
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const [performanceStudent, setPerformanceStudent] = useState(null);
  const [performanceTab, setPerformanceTab] = useState("hw"); // 'hw' | 'test'
  const [studentHwList, setStudentHwList] = useState([]);
  const [studentTestList, setStudentTestList] = useState([]);
  const [loadingPerformance, setLoadingPerformance] = useState(false);

  // State variables for managing dropdown lists inside the Modal
  const [expandedClassId, setExpandedClassId] = useState(null);
  const [classDetails, setClassDetails] = useState({ lessons: [], absences: [] });
  const [loadingDetails, setLoadingDetails] = useState(false);

  // --- Mass Booking Scheduler Engine States ---
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingStudentSearch, setBookingStudentSearch] = useState("");
  const [bookingSelectedStudent, setBookingSelectedStudent] = useState(null);
  const [bookingRemainingTokens, setBookingRemainingTokens] = useState(0);
  const [filteredClassOptions, setFilteredClassOptions] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [preferredStartTime, setPreferredStartTime] = useState("15:00");
  const [lessonsPerDay, setLessonsPerDay] = useState(2);
  const [preferredDays, setPreferredDays] = useState({
    monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false
  });
  const [proposedList, setProposedList] = useState([]);
  const [generatingProposal, setGeneratingProposal] = useState(false);

  // --- New Course for Student Modal States ---
  const [isCreateCourseModalOpen, setIsCreateCourseModalOpen] = useState(false);
  const [createCourseStudentSearch, setCreateCourseStudentSearch] = useState("");
  const [createCourseSelectedStudent, setCreateCourseSelectedStudent] = useState(null);
  const [selectedNewCourseId, setSelectedNewCourseId] = useState("");
  const [creatingCourse, setCreatingCourse] = useState(false);

  // --- Real-time RowSlots Cache ---
  const [slotsCache, setSlotsCache] = useState({});

  const fetchAndCacheSlotsForDate = async (targetDate) => {
    if (!targetDate || slotsCache[targetDate] === "loading" || slotsCache[targetDate] !== undefined) return;

    setSlotsCache(prev => ({ ...prev, [targetDate]: "loading" }));

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getAvailableSlots`, {
        params: { date: targetDate }
      });
     
      setSlotsCache(prev => ({
        ...prev,
        [targetDate]: response.data.success ? (response.data.slots || []) : []
      }));
    } catch (err) {
      console.error(`Error fetching slots for ${targetDate}:`, err);
      setSlotsCache(prev => ({ ...prev, [targetDate]: [] }));
    }
  };

  useEffect(() => {
    proposedList.forEach(item => {
      if (item.date) fetchAndCacheSlotsForDate(item.date);
    });
  }, [proposedList]);

  const handleDeleteVideoClass = async (videoClassId) => {
    if (!window.confirm(`Are you sure you want to delete video class ${videoClassId}?`)) return;
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/deleteVideoClass`, {
        video_class_id: videoClassId,
      });
      if (response.data.success) {
        setVideoClasses(videoClasses.filter(vc => vc.video_class_id !== videoClassId));
        alert("Video class deleted successfully.");
      } else {
        alert(response.data.message || "Failed to delete video class.");
      }
    } catch (error) {
      console.error("Error deleting video class:", error);
      alert("An error occurred while deleting the video class.");
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getCourse`);
        setCourses(response.data.data);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };

    const fetchStudents = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudent`);
        setStudents(response.data.data);
      } catch (error) {
        console.error('Error fetching students:', error);
      }
    };

    const fetchVideoClasses = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getVideoClass`);
        setVideoClasses(response.data.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };

    fetchCourses();
    fetchStudents();
    fetchVideoClasses();
  }, []);

  const vcCourses = courses.filter(
    (c) => c.course_id && String(c.course_id).toLowerCase().startsWith("vc")
  );

  const filteredStudents = students.filter((s) => {
    const matchesSearch = `${s.student_login_id} ${s.student_id} ${s.surname} ${s.first_name} ${s.chinese_name} ${s.school}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const indexOfLast = currentPage * StudentsPerPage;
  const indexOfFirst = indexOfLast - StudentsPerPage;
  const currentStudent = filteredStudents.slice(indexOfFirst, indexOfLast);

  const handleOpenStudentModal = async (student) => {
    setSelectedStudent(student);
    setExpandedClassId(null);
    setClassDetails({ lessons: [], absences: [] });
    setStudentModalTab('classes');
    
    // Fetch paid courses for the student profile tab
    setLoadingPaidCourses(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentPaidCourses`, {
        params: { student_id: student.student_id }
      });
      if (response.data.success) {
        setStudentPaidCourses(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching paid courses:", error);
      setStudentPaidCourses([]);
    } finally {
      setLoadingPaidCourses(false);
    }
  };

  // --- Open Student Performance Tracker Modal ---
  const handleOpenPerformanceModal = async (student) => {
    setPerformanceStudent(student);
    setIsPerformanceModalOpen(true);
    setPerformanceTab("hw");
    setLoadingPerformance(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentHwTests`, {
        params: { student_id: student.student_id }
      });
      if (response.data.success) {
        setStudentHwList(response.data.homework);
        setStudentTestList(response.data.tests);
      }
    } catch (error) {
      console.error("Error fetching performance records:", error);
      alert("Failed to retrieve performance tracking records.");
    } finally {
      setLoadingPerformance(false);
    }
  };

  const handlePerformanceFieldChange = async (type, name, field, value) => {
    if (type === 'hw') {
      setStudentHwList(prev => prev.map(item => item.hw_name === name ? { ...item, [field]: value } : item));
    } else {
      setStudentTestList(prev => prev.map(item => item.test_name === name ? { ...item, [field]: value } : item));
    }

    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/admin/updateHwTestRecord`, {
        student_id: performanceStudent.student_id,
        type,
        name,
        field,
        value
      });
    } catch (error) {
      console.error("Error updating performance record:", error);
      alert("Failed to save changes.");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const handleToggleDropdown = async (videoClassId) => {
    if (expandedClassId === videoClassId) {
      setExpandedClassId(null);
      return;
    }
    setExpandedClassId(videoClassId);
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getVideoLessonsDetails`, {
        params: { video_class_id: videoClassId }
      });
      if (response.data.success) {
        setClassDetails({
          lessons: response.data.lessons,
          absences: response.data.absences
        });
      }
    } catch (error) {
      console.error("Error fetching video lesson details:", error);
      alert("Failed to retrieve lesson dropdown details.");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleStatusChange = async (videoLessonId, videoClassId, newStatus) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/updateLessonStatus`, {
        video_lesson_id: videoLessonId,
        video_class_id: videoClassId,
        completion_status: newStatus
      });
      if (response.data.success) {
        setClassDetails(prev => ({
          ...prev,
          lessons: prev.lessons.map(lesson =>
            lesson.video_lesson_id === videoLessonId
              ? { ...lesson, completion_status: newStatus }
              : lesson
          )
        }));
        setVideoClasses(prevClasses =>
          prevClasses.map(vc =>
            vc.video_class_id === videoClassId
              ? { ...vc, progress: response.data.newProgress }
              : vc
          )
        );
      } else {
        alert(response.data.message || "Failed to update status.");
      }
    } catch (error) {
      console.error("Error updating lesson status:", error);
      alert("An error occurred while changing completion status.");
    }
  };

  const handleDeleteBooking = async (e, videoLessonId, videoClassId) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this lesson booking? This resets status to 'not reg'.")) return;
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/deleteLessonBooking`, {
        video_lesson_id: videoLessonId,
        video_class_id: videoClassId
      });
      if (response.data.success) {
        setClassDetails(prev => ({
          ...prev,
          lessons: prev.lessons.map(lesson =>
            lesson.video_lesson_id === videoLessonId
              ? { ...lesson, date: '-', completion_status: 'not reg' }
              : lesson
          )
        }));
        setVideoClasses(prevClasses =>
          prevClasses.map(vc =>
            vc.video_class_id === videoClassId
              ? { ...vc, progress: response.data.newProgress }
              : vc
          )
        );
        alert("Booking removed successfully.");
      } else {
        alert(response.data.message || "Failed to delete booking.");
      }
    } catch (error) {
      console.error("Error removing booking:", error);
      alert("An error occurred while attempting to delete the booking.");
    }
  };

  const handleSelectBookingStudent = async (student) => {
    setBookingSelectedStudent(student);
    setBookingStudentSearch(`${student.surname} ${student.first_name} (${student.student_id})`);

    // Fetch remaining video tokens immediately upon selection
    try {
      const tokenRes = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentVideoToken/${student.student_id}`);
      if (tokenRes.data.success) {
        setBookingRemainingTokens(tokenRes.data.data);
      }
    } catch (err) {
      console.error("Error fetching tokens:", err);
      setBookingRemainingTokens(0);
    }

    const availableClasses = videoClasses.filter(vc => {
      if (vc.student_id !== student.student_id) return false;
      const progressFraction = vc.progress.split('/');
      if (progressFraction.length === 2 && progressFraction[0] === progressFraction[1]) return false;
      return true;
    });

    const optionsPayload = availableClasses.map(vc => {
      const targetCourse = courses.find(c => c.course_id === vc.course_id);
      return {
        video_class_id: vc.video_class_id,
        course_name: targetCourse ? targetCourse.course_name : `Course Ref: ${vc.video_class_id}`
      };
    });

    setFilteredClassOptions(optionsPayload);
    setSelectedClassId("");
    setProposedList([]);
  };

  const handleCalculateScheduleProposal = async () => {
    if (!selectedClassId || !startDate || !preferredStartTime) {
      alert("Please map target Student details, active course items, and parameters before generating.");
      return;
    }
    const hasActiveDaysChecked = Object.values(preferredDays).some(v => v === true);
    if (!hasActiveDaysChecked) {
      alert("Please select at least one active weekday parameter.");
      return;
    }

    setGeneratingProposal(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/generateProposedSchedule`, {
        video_class_id: selectedClassId,
        start_date: startDate,
        preferred_days: preferredDays,
        preferred_start_time: preferredStartTime,
        lessons_per_day: lessonsPerDay
      });

      if (response.data.success) {
        const generatedSchedule = response.data.proposedSchedule || [];
        setProposedList(generatedSchedule);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Error running configuration engine calculations.");
    } finally {
      setGeneratingProposal(false);
    }
  };

  const handleProposalDateChangeInline = async (targetIndex, updatedDateValue) => {
    const placeholderArray = [...proposedList];
    placeholderArray[targetIndex].date = updatedDateValue;

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/recalculateScheduleChain`, {
        current_proposals: placeholderArray,
        changed_index: targetIndex,
        preferred_days: preferredDays,
        preferred_start_time: preferredStartTime,
        lessons_per_day: lessonsPerDay
      });
      if (response.data.success) {
        setProposedList(response.data.proposedSchedule);
      }
    } catch (err) {
      console.error("Recalculation error:", err);
    }
  };

  const handleProposalTimeChangeInline = (targetIndex, updatedTimeValue) => {
    setProposedList(prevList =>
      prevList.map((item, idx) =>
        idx === targetIndex ? { ...item, timeslot_label: updatedTimeValue } : item
      )
    );
  };

  const handleRemoveLastProposal = () => {
    if (proposedList.length === 0) return;
    setProposedList(prev => prev.slice(0, -1));
  };

  const handleFinalizeMassBookingSave = async () => {
    if (proposedList.length === 0) return;
    if (!window.confirm(`Commit generation tracking entries block booking initialization processing for ${proposedList.length} lesson slots?`)) return;

    try {
      let savedRecordsCounter = 0;
      const targetCourseName = filteredClassOptions.find(o => o.video_class_id === selectedClassId)?.course_name || "";

      for (let row of proposedList) {
        const structuralPayload = {
          student_id: bookingSelectedStudent.student_id,
          date: row.date,
          current_video_course: targetCourseName,
          timeslot: row.timeslot_label,
          video_lesson_id: row.video_lesson_id
        };
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/createBooking`, structuralPayload);
        savedRecordsCounter++;
      }

      alert(`Mass Booking adjustments successfully committed! ${savedRecordsCounter} records updated.`);
      setIsBookingModalOpen(false);
      setProposedList([]);
      setBookingSelectedStudent(null);
      setBookingStudentSearch("");
     
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getVideoClass`);
      setVideoClasses(response.data.data);
    } catch (err) {
      console.error("Batch processing runtime exception detected:", err);
      alert("Mass booking processing encountered error sequences. Review output table structures.");
    }
  };

  const handleSelectCreateCourseStudent = (student) => {
    setCreateCourseSelectedStudent(student);
    setCreateCourseStudentSearch(`${student.surname} ${student.first_name} (${student.student_id})`);
  };

  const handleCreateVideoCourse = async () => {
    if (!createCourseSelectedStudent) {
      alert("Please select a student.");
      return;
    }
    if (!selectedNewCourseId) {
      alert("Please select a course.");
      return;
    }

    setCreatingCourse(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/createVideoCourse`, {
        student_id: createCourseSelectedStudent.student_id,
        course_id: selectedNewCourseId
      });

      if (response.data.success) {
        alert(response.data.message || "Video course created successfully for the student.");
        const vcRes = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getVideoClass`);
        setVideoClasses(vcRes.data.data);
        setIsCreateCourseModalOpen(false);
        setCreateCourseSelectedStudent(null);
        setCreateCourseStudentSearch("");
        setSelectedNewCourseId("");
      } else {
        alert(response.data.message || "Failed to create video course.");
      }
    } catch (err) {
      console.error("Error creating video course:", err);
      alert(err.response?.data?.message || "An error occurred while creating the video course.");
    } finally {
      setCreatingCourse(false);
    }
  };

  const resetCreateCourseModal = () => {
    setIsCreateCourseModalOpen(false);
    setCreateCourseSelectedStudent(null);
    setCreateCourseStudentSearch("");
    setSelectedNewCourseId("");
  };

  const studentVideoClasses = selectedStudent ? videoClasses.filter(vc => vc.student_id === selectedStudent.student_id) : [];

  const formatLocalDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="p-8 min-h-screen overflow-y-auto text-slate-800">
      <h1 className="text-2xl font-bold">Video Class Management</h1>
      <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>

      {/* Search Bar & Action Buttons Container */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between md:gap-4">
        <input
          type="text"
          placeholder="Search by name, ID or Date"
          value={searchQuery}
          onChange={handleSearch}
          className="w-[300px] px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <div className="flex flex-col sm:flex-row gap-2 mt-2 md:mt-0">
          <button
            onClick={() => setIsCreateCourseModalOpen(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-md shadow transition-all"
          >
            <PlusCircle className="w-4 h-4" /> New Course for Student
          </button>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-md shadow transition-all"
          >
            <CalendarPlus className="w-4 h-4" /> Mass Lesson Booking
          </button>
        </div>
      </div>

      {/* Student Table */}
      <table className="w-full border-collapse bg-white shadow rounded">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-4 py-2">Student LogIn ID</th>
            <th className="border px-4 py-2">Student ID</th>
            <th className="border px-4 py-2">Surname</th>
            <th className="border px-4 py-2">First Name</th>
            <th className="border px-4 py-2">Chinese Name</th>
            <th className="border px-4 py-2">Form</th>
            <th className="border px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentStudent.map(student => (
            <tr key={student.student_id} className="text-center border-t">
              <td className="border px-4 py-2">{student.student_login_id}</td>
              <td className="border px-4 py-2">{student.student_id}</td>
              <td className="border px-4 py-2">{student.surname}</td>
              <td className="border px-4 py-2">{student.first_name}</td>
              <td className="border px-4 py-2">{student.chinese_name || '-'}</td>
              <td className="border px-4 py-2">{student.grade || '-'}</td>
              <td className="border px-4 py-2">
                <div className="flex items-center justify-center gap-3">
                  <button onClick={() => handleOpenStudentModal(student)} title="View Video Classes">
                    <Pencil className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                  </button>
                  <button onClick={() => handleOpenPerformanceModal(student)} title="Student Performance Tracker">
                    <FileText className="w-5 h-5 text-emerald-600 hover:text-emerald-800" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* --- Student Performance Tracker Modal --- */}
      {isPerformanceModalOpen && performanceStudent && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white w-full max-w-5xl p-6 rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-xl font-bold text-gray-800">
                Student Performance Tracker: {performanceStudent.surname} {performanceStudent.first_name} ({performanceStudent.chinese_name || ''})
              </h2>
              <button onClick={() => setIsPerformanceModalOpen(false)} className="text-gray-500 hover:text-gray-700 font-bold text-lg">✕</button>
            </div>

            <div className="flex gap-2 mb-4 border-b pb-2">
              <button
                onClick={() => setPerformanceTab("hw")}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${performanceTab === 'hw' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                HW Records ({studentHwList.length})
              </button>
              <button
                onClick={() => setPerformanceTab("test")}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${performanceTab === 'test' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Test Records ({studentTestList.length})
              </button>
            </div>

            {loadingPerformance ? (
              <p className="text-center py-8 text-gray-500">Loading records...</p>
            ) : performanceTab === 'hw' ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white text-sm text-left">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="p-2 border">Related Course</th>
                      <th className="p-2 border">HW Name</th>
                      <th className="p-2 border">Issue Date</th>
                      <th className="p-2 border">Due Date</th>
                      <th className="p-2 border">Submission Date</th>
                      <th className="p-2 border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentHwList.length === 0 ? (
                      <tr><td colSpan="6" className="text-center p-4 text-gray-500">No homework records found.</td></tr>
                    ) : (
                      studentHwList.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2 border">{item.related_course || '-'}</td>
                          <td className="p-2 border font-medium">{item.hw_name}</td>
                          <td className="p-2 border">
                            <input
                              type="date"
                              value={formatLocalDate(item.issue_date)}
                              onChange={(e) => handlePerformanceFieldChange('hw', item.hw_name, 'issue_date', e.target.value)}
                              className="border p-1 rounded text-xs w-full"
                            />
                          </td>
                          <td className="p-2 border">
                            <input
                              type="date"
                              value={formatLocalDate(item.due_date)}
                              onChange={(e) => handlePerformanceFieldChange('hw', item.hw_name, 'due_date', e.target.value)}
                              className="border p-1 rounded text-xs w-full"
                            />
                          </td>
                          <td className="p-2 border">
                            <input
                              type="date"
                              value={formatLocalDate(item.submission_date)}
                              onChange={(e) => handlePerformanceFieldChange('hw', item.hw_name, 'submission_date', e.target.value)}
                              className="border p-1 rounded text-xs w-full"
                            />
                          </td>
                          <td className="p-2 border">
                            <select
                              value={item.status || 'pending'}
                              onChange={(e) => handlePerformanceFieldChange('hw', item.hw_name, 'status', e.target.value)}
                              className="border p-1 rounded text-xs w-full bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="pending">pending</option>
                              <option value="handed-in">handed-in</option>
                              <option value="late-sbumission">late-sbumission</option>
                              <option value="waived">waived</option>
                              <option value="needs discussion">needs discussion</option>
                            </select>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white text-sm text-left">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="p-2 border">Related Course</th>
                      <th className="p-2 border">Test Name</th>
                      <th className="p-2 border">Issue Date</th>
                      <th className="p-2 border">Due Date</th>
                      <th className="p-2 border">Submission Date</th>
                      <th className="p-2 border">Status</th>
                      <th className="p-2 border">Mark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentTestList.length === 0 ? (
                      <tr><td colSpan="7" className="text-center p-4 text-gray-500">No test records found.</td></tr>
                    ) : (
                      studentTestList.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2 border">{item.related_course || '-'}</td>
                          <td className="p-2 border font-medium">{item.test_name}</td>
                          <td className="p-2 border">
                            <input
                              type="date"
                              value={formatLocalDate(item.issue_date)}
                              onChange={(e) => handlePerformanceFieldChange('test', item.test_name, 'issue_date', e.target.value)}
                              className="border p-1 rounded text-xs w-full"
                            />
                          </td>
                          <td className="p-2 border">
                            <input
                              type="date"
                              value={formatLocalDate(item.due_date)}
                              onChange={(e) => handlePerformanceFieldChange('test', item.test_name, 'due_date', e.target.value)}
                              className="border p-1 rounded text-xs w-full"
                            />
                          </td>
                          <td className="p-2 border">
                            <input
                              type="date"
                              value={formatLocalDate(item.submission_date)}
                              onChange={(e) => handlePerformanceFieldChange('test', item.test_name, 'submission_date', e.target.value)}
                              className="border p-1 rounded text-xs w-full"
                            />
                          </td>
                          <td className="p-2 border">
                            <select
                              value={item.status || 'pending'}
                              onChange={(e) => handlePerformanceFieldChange('test', item.test_name, 'status', e.target.value)}
                              className="border p-1 rounded text-xs w-full bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="pending">pending</option>
                              <option value="handed-in">handed-in</option>
                              <option value="late-sbumission">late-sbumission</option>
                              <option value="waived">waived</option>
                              <option value="needs discussion">needs discussion</option>
                            </select>
                          </td>
                          <td className="p-2 border">
                            <input
                              type="text"
                              value={item.mark !== null && item.mark !== undefined ? item.mark : ''}
                              onChange={(e) => handlePerformanceFieldChange('test', item.test_name, 'mark', e.target.value)}
                              className="border p-1 rounded text-xs w-20"
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button onClick={() => setIsPerformanceModalOpen(false)} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Student Modal Details Wrapper Section with Two Tabs */}
      {selectedStudent && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[70]">
          <div className="bg-white w-11/12 max-w-6xl p-6 rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-xl font-bold">
                Student Profile: {selectedStudent.surname} {selectedStudent.first_name} ({selectedStudent.chinese_name || ''})
              </h2>
              <button onClick={() => setSelectedStudent(null)} className="text-gray-500 hover:text-gray-700 font-bold text-lg">✕</button>
            </div>

            {/* Modal Tabs Selection */}
            <div className="flex gap-2 mb-4 border-b pb-2">
              <button
                onClick={() => setStudentModalTab('classes')}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${studentModalTab === 'classes' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Video Classes Record ({studentVideoClasses.length})
              </button>
              <button
                onClick={() => setStudentModalTab('payment')}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${studentModalTab === 'payment' ? 'bg-blue-600 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Video Payment Record
              </button>
            </div>

            {studentModalTab === 'classes' ? (
              <table className="w-full border-collapse bg-white mb-4">
                <thead>
                  <tr className="bg-gray-200 text-center">
                    <th className="border px-4 py-2 text-left w-1/4">Video Class ID</th>
                    <th className="border px-4 py-2">Course Name</th>
                    <th className="border px-4 py-2">Progress</th>
                    <th className="border px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {studentVideoClasses.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="border px-4 py-2 text-center text-gray-500">No video classes found for this student.</td>
                    </tr>
                  ) : (
                    studentVideoClasses.map(vc => (
                      <React.Fragment key={vc.video_class_id}>
                        <tr
                          onClick={() => handleToggleDropdown(vc.video_class_id)}
                          className="border-t bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none text-center"
                        >
                          <td className="border px-4 py-2 text-left">
                            <div className="flex items-center gap-3">
                              <span className="text-gray-500">
                                {expandedClassId === vc.video_class_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </span>
                              <span className="font-semibold text-gray-800">{vc.video_class_id}</span>
                            </div>
                          </td>
                          <td className="border px-4 py-2">
                            {courses.find(course => course.course_id === vc.course_id)?.course_name || 'N/A'}
                          </td>
                          <td className="border px-4 py-2 font-bold text-blue-600">{vc.progress}</td>
                          <td className="border px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => handleDeleteVideoClass(vc.video_class_id)}>
                              <Trash2 className="w-5 h-5 text-red-600 hover:text-red-800 m-auto" />
                            </button>
                          </td>
                        </tr>
                        {expandedClassId === vc.video_class_id && (
                          <tr>
                            <td colSpan="4" className="border px-6 py-4 bg-gray-50 text-left" onClick={(e) => e.stopPropagation()}>
                              {loadingDetails ? (
                                <div className="text-center text-gray-500 py-2">Loading details...</div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div>
                                    <h4 className="font-semibold text-blue-800 mb-2 border-b pb-1">Video Lessons List</h4>
                                    <div className="max-h-60 overflow-y-auto border rounded bg-white">
                                      <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-100 sticky top-0">
                                          <tr>
                                            <th className="p-2 border-b">Lesson ID</th>
                                            <th className="p-2 border-b">Status</th>
                                            <th className="p-2 border-b">Booking Date</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {classDetails.lessons.map((lesson) => (
                                            <tr key={lesson.video_lesson_id} className="border-b hover:bg-gray-50">
                                              <td className="p-2 font-mono text-xs font-semibold text-blue-700">
                                                {lesson.video_lesson_id ? lesson.video_lesson_id.slice(-3) : '-'}
                                              </td>
                                              <td className="p-1">
                                                <select
                                                  value={lesson.completion_status || 'not reg'}
                                                  onChange={(e) => handleStatusChange(lesson.video_lesson_id, vc.video_class_id, e.target.value)}
                                                  className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                                                >
                                                  <option value="completed">completed</option>
                                                  <option value="pending">pending</option>
                                                  <option value="not reg">not reg</option>
                                                </select>
                                              </td>
                                              <td className="p-2">
                                                <div className="flex items-center gap-3">
                                                  <span>{lesson.date}
                                                    {lesson.period && (
                                                      <span className="ml-2 text-xs text-gray-500">
                                                        {lesson.period}
                                                      </span>
                                                    )}
                                                  </span>         
                                                  {lesson.date !== '-' && (
                                                    <button
                                                      onClick={(e) => handleDeleteBooking(e, lesson.video_lesson_id, vc.video_class_id)}
                                                      className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                                      title="Delete Booking Record"
                                                    >
                                                      <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-red-800 mb-2 border-b pb-1">Absence Logs (Active Records)</h4>
                                    <div className="max-h-60 overflow-y-auto border rounded bg-white">
                                      {classDetails.absences.length === 0 ? (
                                        <p className="p-4 text-sm text-gray-500 text-center">No active absence records found.</p>
                                      ) : (
                                        <table className="w-full text-sm text-left">
                                          <thead className="bg-gray-100 sticky top-0">
                                            <tr>
                                              <th className="p-2 border-b">Lesson ID</th>
                                              <th className="p-2 border-b">Absence Date</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {classDetails.absences.map((abs, idx) => (
                                              <tr key={`${abs.video_lesson_id}-${idx}`} className="border-b hover:bg-red-50">
                                                <td className="p-2 font-mono text-xs font-semibold text-red-700">
                                                  {abs.video_lesson_id ? abs.video_lesson_id.slice(-3) : '-'}
                                                </td>
                                                <td className="p-2">{abs.absence_date}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <div className="overflow-x-auto mb-4">
                {loadingPaidCourses ? (
                  <p className="text-center py-8 text-gray-500">Loading payment records...</p>
                ) : (
                  <table className="w-full border-collapse bg-white text-sm text-left">
                    <thead>
                      <tr className="bg-gray-100 border-b">
                        <th className="p-3 border">Video Invoice Ref</th>
                        <th className="p-3 border">Course Name</th>
                        <th className="p-3 border">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentPaidCourses.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center p-4 text-gray-500">No paid course records found.</td>
                        </tr>
                      ) : (
                        studentPaidCourses.map((payItem, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 border font-mono font-semibold text-indigo-700">{payItem.video_invoice_ref}</td>
                            <td className="p-3 border">{payItem.course_name}</td>
                            <td className="p-3 border">
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold uppercase">
                                {payItem.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <button onClick={() => setSelectedStudent(null)} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* --- Mass Advanced Scheduler Popup Overlay Interface Core View Layout Layer --- */}
      {isBookingModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-4">
          <div className="bg-white w-full max-w-4xl p-6 rounded-xl shadow-2xl relative max-h-[92vh] overflow-y-auto text-left">
            <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
                <CalendarPlus className="text-emerald-600 w-5 h-5" /> Mass Booking
              </h2>
              
              {/* Top-Right Corner Token Display and Open Profile Button */}
              {bookingSelectedStudent && (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleOpenStudentModal(bookingSelectedStudent)}
                    className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded text-xs font-semibold transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> open profile
                  </button>
                  <div className="px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded text-xs font-bold shadow-sm">
                    Remaining Video Token : {bookingRemainingTokens}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="relative">
                <label className="flex items-center gap-1 text-xs font-bold mb-1 text-gray-700 uppercase tracking-wide"><User className="w-3.5 h-3.5 text-gray-400" /> Student Selection</label>
                <input
                  type="text"
                  placeholder="Search student login code IDs or surname fields..."
                  value={bookingStudentSearch}
                  onChange={(e) => { setBookingStudentSearch(e.target.value); setBookingSelectedStudent(null); }}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                />
                {!bookingSelectedStudent && bookingStudentSearch && (
                  <div className="absolute left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg z-50">
                    {students.filter(s => `${s.student_id} ${s.surname} ${s.first_name}`.toLowerCase().includes(bookingStudentSearch.toLowerCase())).map(st => (
                      <div key={st.student_id} onClick={() => handleSelectBookingStudent(st)} className="p-2 cursor-pointer border-b border-gray-100 text-xs flex justify-between hover:bg-slate-50">
                        <div>
                          <strong className="text-blue-700 font-mono mr-2">{st.student_id}</strong>
                          <span className="font-medium text-gray-700">{st.surname} {st.first_name}</span>
                        </div>
                        <span className="text-gray-400 font-medium italic">{st.chinese_name || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-1 text-xs font-bold mb-1 text-gray-700 uppercase tracking-wide"><BookOpen className="w-3.5 h-3.5 text-gray-400" /> Course Selection</label>
                <select
                  disabled={!bookingSelectedStudent}
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 h-[38px]"
                >
                  <option value="">-- Please select course --</option>
                  {filteredClassOptions.map(opt => (
                    <option key={opt.video_class_id} value={opt.video_class_id}>{opt.course_name} (ID: {opt.video_class_id})</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-600">Target Start Date</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full p-1.5 border border-gray-300 rounded text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-600">Preferred Start Time</label>
                  <select
                    value={preferredStartTime}
                    onChange={(e) => setPreferredStartTime(e.target.value)}
                    className="w-full p-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 h-[38px]"
                  >
                    {[
                      "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
                      "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
                      "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
                      "18:00", "18:30", "19:00", "19:30", "20:00", "20:30"
                    ].map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-600">Max Lessons Allocated / Day</label>
                  <input type="number" min="1" max="4" value={lessonsPerDay} onChange={(e) => setLessonsPerDay(e.target.value)} className="w-full p-1.5 border border-gray-300 rounded text-sm focus:outline-none font-mono font-bold" />
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-semibold mb-1 text-gray-600">Weekday Allocations</label>
                <div className="flex flex-wrap gap-3 bg-white border border-gray-300 p-2 rounded text-sm">
                  {Object.keys(preferredDays).map(day => (
                    <label key={day} className="capitalize flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-700">
                      <input type="checkbox" checked={preferredDays[day]} onChange={() => setPreferredDays(prev => ({ ...prev, [day]: !prev[day] }))} className="rounded" />
                      {day.slice(0, 3)}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button type="button" onClick={handleCalculateScheduleProposal} disabled={generatingProposal} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded shadow-sm transition-colors disabled:opacity-50">
                  {generatingProposal ? "Running configuration algorithms..." : "Generate Schedule"}
                </button>
              </div>
            </div>

            {proposedList.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-gray-700 mb-2 flex items-center justify-between">
                  Proposed Schedule Matrix
                  <span className="text-xs font-normal text-gray-500">
                    ({proposedList.length} lessons)
                  </span>
                </h3>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg shadow-inner bg-white">
                  <table className="w-full text-xs text-left border-collapse">
                    <thead className="bg-slate-100 sticky top-0 border-b border-gray-200">
                      <tr>
                        <th className="p-2 font-bold text-gray-600">Lesson ID</th>
                        <th className="p-2 font-bold text-gray-600">Target Date</th>
                        <th className="p-2 font-bold text-gray-600">Allocated Dynamic Time Slot</th>
                        <th className="p-2 w-10 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposedList.map((item, index) => {
                        const cachedSlots = slotsCache[item.date];
                        const isLoading = cachedSlots === "loading" || !cachedSlots;
                        const isLastRow = index === proposedList.length - 1;

                        return (
                          <tr key={index} className="border-b border-gray-100 hover:bg-slate-50 transition-colors">
                            <td className="p-2 font-mono text-blue-700 font-bold">{item.video_lesson_id.slice(-3)}</td>
                            <td className="p-2">
                              <input
                                type="date"
                                value={item.date}
                                onChange={(e) => handleProposalDateChangeInline(index, e.target.value)}
                                className="p-1 border border-gray-300 rounded text-xs focus:outline-none bg-slate-50 focus:bg-white"
                              />
                            </td>
                            <td className="p-2">
                              <select
                                value={item.timeslot_label || ""}
                                onChange={(e) => handleProposalTimeChangeInline(index, e.target.value)}
                                disabled={isLoading}
                                className="p-1 border border-gray-300 rounded text-xs w-48 font-semibold bg-slate-50 focus:bg-white focus:outline-none h-[28px] disabled:opacity-60"
                              >
                                {isLoading ? (
                                  <option value="">Loading slots...</option>
                                ) : cachedSlots.length === 0 ? (
                                  <option value="">No free slots for this date</option>
                                ) : (
                                  <>
                                    {item.timeslot_label && !cachedSlots.includes(item.timeslot_label) && (
                                      <option value={item.timeslot_label}>{item.timeslot_label}</option>
                                    )}
                                    {cachedSlots.map((slot, sIdx) => (
                                      <option key={sIdx} value={slot}>{slot}</option>
                                    ))}
                                  </>
                                )}
                              </select>
                            </td>
                            <td className="p-2 text-center">
                              {isLastRow && (
                                <button
                                  onClick={handleRemoveLastProposal}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                                  title="Remove last lesson"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 border-t border-gray-200 pt-3 mt-4">
              <button
                type="button"
                onClick={() => {
                  setIsBookingModalOpen(false);
                  setProposedList([]);
                  setBookingSelectedStudent(null);
                  setBookingStudentSearch("");
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFinalizeMassBookingSave}
                disabled={proposedList.length === 0}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded shadow disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                Save Bookings block
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- New Course for Student Modal --- */}
      {isCreateCourseModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-4">
          <div className="bg-white w-full max-w-lg p-6 rounded-xl shadow-2xl relative text-left">
            <h2 className="text-lg font-bold border-b border-gray-200 pb-3 mb-4 flex items-center gap-2 text-gray-800">
              <PlusCircle className="text-indigo-600 w-5 h-5" /> New Course for Student
            </h2>

            <div className="space-y-4 mb-6">
              <div className="relative">
                <label className="flex items-center gap-1 text-xs font-bold mb-1 text-gray-700 uppercase tracking-wide">
                  <User className="w-3.5 h-3.5 text-gray-400" /> Student Selection
                </label>
                <input
                  type="text"
                  placeholder="Search student login code IDs or surname fields..."
                  value={createCourseStudentSearch}
                  onChange={(e) => {
                    setCreateCourseStudentSearch(e.target.value);
                    setCreateCourseSelectedStudent(null);
                  }}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm"
                />
                {!createCourseSelectedStudent && createCourseStudentSearch && (
                  <div className="absolute left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg z-50">
                    {students
                      .filter((s) =>
                        `${s.student_id} ${s.surname} ${s.first_name}`
                          .toLowerCase()
                          .includes(createCourseStudentSearch.toLowerCase())
                      )
                      .map((st) => (
                        <div
                          key={st.student_id}
                          onClick={() => handleSelectCreateCourseStudent(st)}
                          className="p-2 cursor-pointer border-b border-gray-100 text-xs flex justify-between hover:bg-slate-50"
                        >
                          <div>
                            <strong className="text-indigo-700 font-mono mr-2">{st.student_id}</strong>
                            <span className="font-medium text-gray-700">
                              {st.surname} {st.first_name}
                            </span>
                          </div>
                          <span className="text-gray-400 font-medium italic">{st.chinese_name || ""}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-1 text-xs font-bold mb-1 text-gray-700 uppercase tracking-wide">
                  <BookOpen className="w-3.5 h-3.5 text-gray-400" /> Course Selection
                </label>
                <select
                  value={selectedNewCourseId}
                  onChange={(e) => setSelectedNewCourseId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 h-[38px]"
                >
                  <option value="">-- Please select a VC course --</option>
                  {vcCourses.map((c) => (
                    <option key={c.course_id} value={c.course_id}>
                      {c.course_name} ({c.course_id})
                    </option>
                  ))}
                </select>
                {vcCourses.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">No courses with course_id starting with "vc" found.</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
              <button
                type="button"
                onClick={resetCreateCourseModal}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateVideoCourse}
                disabled={creatingCourse || !createCourseSelectedStudent || !selectedNewCourseId}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded shadow disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                {creatingCourse ? "Creating..." : "Create Course"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}